import { supabase, Database } from './supabase';
import { getPublicUrl } from './storageHelpers';
import { ANIMATIONS_PER_PAGE } from '../pages/animate/components/AnimateGallery';
import { SupabaseClient } from '@supabase/supabase-js';

export type AnimationOverview = Database['public']['Tables']['animation']['Row'] & {
  referenceImage?: string | null;
  animationVideo?: string | null;
  user_tag?: string | null;
};

/**
 * Lists animations across the platform that HAVE a video in their LATEST history entry.
 * Excludes hidden, deleted, and private items.
 */
export const fetchAllAnimations = async (
    offset: number = 0,
    pageSize: number = ANIMATIONS_PER_PAGE
  ): Promise<{ animations: AnimationOverview[]; hasMore: boolean; nextOffset: number }> => {
    try {
      let accumulatedAnimations: AnimationOverview[] = [];
      let currentScanOffset = offset;
      let hasMoreFromDb = true;
      let iterations = 0;
      const MAX_ITERATIONS = 5; // Safety guard to prevent runaway loops

      while (accumulatedAnimations.length < pageSize && hasMoreFromDb && iterations < MAX_ITERATIONS) {
        iterations++;
        const start = currentScanOffset;
        const end = start + (pageSize * 2) - 1; // Over-fetch to find valid items faster

        const { data: animData, error: animError, count } = await supabase
          .from('animation')
          .select(`
            *,
            animation_history_entries (
              animation_id, 
              reference_image, 
              animation_video, 
              updated_at
            )
          `, { count: 'exact' })
          .or('deleted.is.null,deleted.eq.false')
          .or('hidden.is.null,hidden.eq.false')
          .or('private.is.null,private.eq.false')
          .order('updated_at', { ascending: false })
          .order('updated_at', { foreignTable: 'animation_history_entries', ascending: false })
          .limit(1, { foreignTable: 'animation_history_entries' })
          .range(start, end);

        if (animError) throw animError;
        if (!animData || animData.length === 0) {
          hasMoreFromDb = false;
          break;
        }

        const transformed = transformAnimationData(animData, supabase, true);
        
        const spaceLeft = pageSize - accumulatedAnimations.length;
        if (transformed.length <= spaceLeft) {
          accumulatedAnimations = [...accumulatedAnimations, ...transformed];
          currentScanOffset = end + 1;
        } else {
          const itemsToTake = transformed.slice(0, spaceLeft);
          accumulatedAnimations = [...accumulatedAnimations, ...itemsToTake];
          const lastAcceptedItem = itemsToTake[itemsToTake.length - 1];
          const lastIdx = animData.findIndex(d => d.id === lastAcceptedItem.id);
          currentScanOffset = start + lastIdx + 1;
        }

        hasMoreFromDb = count !== null ? currentScanOffset < count : false;
        if (accumulatedAnimations.length >= pageSize) break;
      }

      return { 
        animations: accumulatedAnimations, 
        hasMore: hasMoreFromDb,
        nextOffset: currentScanOffset 
      };
    } catch (error) {
      console.error('Error in fetchAllAnimations:', error);
      return { animations: [], hasMore: false, nextOffset: offset };
    }
  };

/**
 * Lists only the SPECIFIC user's animations using the latest history for thumbnails.
 * Includes hidden items but excludes deleted items.
 */
export const fetchMyAnimations = async (
    userId: string,
    offset: number = 0,
    pageSize: number = ANIMATIONS_PER_PAGE
  ): Promise<{ animations: AnimationOverview[]; hasMore: boolean; nextOffset: number }> => {
    try {
      let accumulatedAnimations: AnimationOverview[] = [];
      let currentScanOffset = offset;
      let hasMoreFromDb = true;
      let iterations = 0;
      const MAX_ITERATIONS = 5;

      while (accumulatedAnimations.length < pageSize && hasMoreFromDb && iterations < MAX_ITERATIONS) {
        iterations++;
        const start = currentScanOffset;
        const end = start + (pageSize * 2) - 1;

        const { data: animData, error: animError, count } = await supabase
          .from('animation')
          .select(`
            *,
            animation_history_entries (
              animation_id, 
              reference_image, 
              animation_video, 
              updated_at
            )
          `, { count: 'exact' })
          .eq('user_id', userId)
          .or('deleted.is.null,deleted.eq.false')
          .order('updated_at', { ascending: false })
          .order('updated_at', { foreignTable: 'animation_history_entries', ascending: false })
          .limit(1, { foreignTable: 'animation_history_entries' })
          .range(start, end);

        if (animError) throw animError;
        if (!animData || animData.length === 0) {
          hasMoreFromDb = false;
          break;
        }

        const transformed = transformAnimationData(animData, supabase, false);
        
        const spaceLeft = pageSize - accumulatedAnimations.length;
        if (transformed.length <= spaceLeft) {
          accumulatedAnimations = [...accumulatedAnimations, ...transformed];
          currentScanOffset = end + 1;
        } else {
          const itemsToTake = transformed.slice(0, spaceLeft);
          accumulatedAnimations = [...accumulatedAnimations, ...itemsToTake];
          const lastAcceptedItem = itemsToTake[itemsToTake.length - 1];
          const lastIdx = animData.findIndex(d => d.id === lastAcceptedItem.id);
          currentScanOffset = start + lastIdx + 1;
        }

        hasMoreFromDb = count !== null ? currentScanOffset < count : false;
        if (accumulatedAnimations.length >= pageSize) break;
      }

      return { 
        animations: accumulatedAnimations, 
        hasMore: hasMoreFromDb,
        nextOffset: currentScanOffset 
      };
    } catch (error) {
      console.error('Error in fetchMyAnimations:', error);
      return { animations: [], hasMore: false, nextOffset: offset };
    }
};

function transformAnimationData(animData: any[], supabase: any, requireVideo: boolean): AnimationOverview[] {
  try {
    const animations = animData
        .map((anim: any) => {
          const latest = (anim.animation_history_entries as any[])?.[0];
          
          if (requireVideo && (!latest || !latest.animation_video)) return null;

          let referenceImage = latest?.reference_image || null;
          let animationVideo = latest?.animation_video || null;

          if (referenceImage && !referenceImage.startsWith('http')) {
            referenceImage = getPublicUrl(supabase, referenceImage);
          }
          if (animationVideo && !animationVideo.startsWith('http')) {
            animationVideo = getPublicUrl(supabase, animationVideo);
          }

          const { animation_history_entries, ...rest } = anim;
          return { ...rest, referenceImage, animationVideo };
        })
        .filter((item: AnimationOverview | null): item is AnimationOverview => item !== null);

    console.log('animations', animations);
    return animations;
  } catch (error) {
    console.error('Error in transformAnimationData:', error);
    return [];
  }
}